import { useTranslation } from 'react-i18next';

import { BILLING_ERROR_CODES } from '@aysar/contracts';

import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import { useBillingEventQuery } from '../../services/billing-events/billing-events.api';

const JSON_INDENT = 2;

export interface UseBillingEventPayloadModalLogic {
  /** The vendor's JSON, formatted. `''` when there was none — see `isEmpty`. */
  payload: string;
  /** The delivery carried no payload at all, which is different from not knowing. */
  isEmpty: boolean;
  /** Which delivery this is, for the dialog's own heading. */
  subtitle: string;
  isLoading: boolean;
  /** The read was refused for want of `billing.manage`. */
  isRestricted: boolean;
  /** The delivery is not there any more — a stale link, or a retired row. */
  isGone: boolean;
  error: ApiError | null;
  refetch: () => void;
}

export interface BillingEventPayloadModalArgs {
  eventId: string;
}

/**
 * What the store actually said, for one delivery (P23b3a).
 *
 * ## It fetches on OPEN, and there is no prefetch anywhere
 *
 * The api writes a `billing.event.payload_viewed` audit row on every successful
 * read. So the request has to be the operator's act of opening this dialog —
 * warming a cache from the list would put somebody's name against a disclosure
 * they never made. That is also why the dialog takes an **id** rather than a row:
 * nothing can hand it a payload it did not ask for.
 *
 * ## A 403 here is the api being right, not a failure
 *
 * The row action is already gated on `billing.manage` (N22), so reaching this
 * refusal means the grant was taken away since the page loaded. It gets its own
 * sentence naming the permission rather than a raw message, because "you no
 * longer have this" is an answer and an operator can act on it.
 *
 * ## `undefined` and `{}` are different answers
 *
 * An absent payload means we did not read one; an empty object means the store
 * sent one and it was empty. Rendering both as `{}` would collapse "we do not
 * know" into "there was nothing" at the exact moment somebody is answering a
 * billing dispute with it.
 */
export function useBillingEventPayloadModalLogic({
  eventId,
}: BillingEventPayloadModalArgs): UseBillingEventPayloadModalLogic {
  const { t } = useTranslation('payments');

  const query = useBillingEventQuery(eventId, { skip: eventId === '' });

  const error = query.error === undefined ? null : toApiError(query.error);
  const raw = query.data?.rawPayload;
  const isEmpty = query.data !== undefined && raw === undefined;

  return {
    payload: isEmpty || raw === undefined ? '' : JSON.stringify(raw, null, JSON_INDENT),
    isEmpty,
    subtitle:
      query.data === undefined
        ? ''
        : t('events.payload.meta', {
            type: t(`events.type.${query.data.eventType}`, { defaultValue: query.data.eventType }),
            received: query.data.externalEventId,
          }),
    isLoading: query.isLoading,
    isRestricted: error?.status === 403,
    isGone: error?.code === BILLING_ERROR_CODES.billingEventNotFound,
    error,
    refetch: () => {
      void query.refetch();
    },
  };
}
